import React, { useEffect, useRef } from 'react';
import { COPY } from '../../contracts/copyMap';
import FaceMeshOverlay from './FaceMeshOverlay';
import heroPortrait from '../../assets/auth-hero.jpg';
import heroMesh from '../../assets/authHeroMesh.json';

// Aspect-locked (3:4) portrait + baked aurum face mesh + [MANIFESTO] censor bar
// + inset vignette. The box is the single coordinate space: the image fills it
// edge-to-edge (no object-fit cropping), so the baked landmark fractions map 1:1
// onto FaceMeshOverlay's canvas at any size.
//
// The mesh carries the SAME treatment as the Create scan's analyzing state, but
// from PRE-BAKED landmarks (authHeroMesh.json, generated by
// scripts/bake-hero-mesh.py) — so this never loads the MediaPipe wasm/model.
// That is what makes it affordable on mobile, where it is now the full-bleed
// backdrop of the signed-out splash rather than a desktop-only flourish.
//
// Extracted from MarketingHero so both breakpoints can render it: desktop as a
// ≥1280px centred backdrop, mobile as the full-width splash image.
//
// Cursor drives the mesh on desktop — the highlight follows the pointer
// (wandering on its own when idle) and the mesh dots are gently repelled around
// it. The portrait itself never moves. On touch devices there is no pointer, so
// the mesh simply runs its idle wander.
//
// Props:
//   vignette  false disables the radial vignette. The mobile splash supplies its
//             own bottom-weighted scrim instead, because a centred vignette
//             fights a full-bleed image that has to fade into content below it.
//   scrim     0-100 opacity of the dimming layer under the mesh. Desktop uses
//             50 so the aurum mesh reads against a bright photo. The mobile
//             splash lowers it: there the photo is the hero, and it also sits
//             under a gradient AND the page's film grain, so three darkening
//             layers stacked crush the face to near-black.
const MeshPortrait = ({ className = '', style, vignette = true, scrim = 50 }) => {
  const boxRef = useRef(null);
  // Shared with the mesh canvas rAF: cursor position in box fractions + flags.
  const pointerRef = useRef({ fx: 0.5, fy: 0.45, active: false, interactive: true });

  // Cursor tracking — page-wide, so hovering the auth card or marketing
  // column still steers the mesh. A zero-size rect (display:none) is ignored.
  useEffect(() => {
    const pointer = pointerRef.current; // stable plain object
    const box = boxRef.current;
    let idleTimer = null;

    const onMove = (e) => {
      if (!box) return;
      const r = box.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const fx = (e.clientX - r.left) / r.width;
      const fy = (e.clientY - r.top) / r.height;
      pointer.fx = Math.max(-0.35, Math.min(1.35, fx));
      pointer.fy = Math.max(-0.35, Math.min(1.35, fy));
      pointer.active = true;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        pointer.active = false; // → mesh light drifts on the wander path
      }, 1600);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (idleTimer) clearTimeout(idleTimer);
      pointer.active = false;
    };
  }, []);

  return (
    <div
      ref={boxRef}
      aria-hidden="true"
      className={['relative shrink-0', className].filter(Boolean).join(' ')}
      style={{ aspectRatio: '3 / 4', containerType: 'inline-size', ...style }}
    >
      <img
        src={heroPortrait}
        alt=""
        className="absolute inset-0 w-full h-full"
        draggable={false}
      />
      {/* Scrim under the mesh — dims the portrait so the aurum mesh reads. */}
      <div
        className="absolute inset-0 bg-obsidian"
        style={{ opacity: scrim / 100 }}
      />
      <FaceMeshOverlay
        landmarks={heroMesh.landmarks}
        edges={heroMesh.edges}
        subtle
        pointerRef={pointerRef}
        className="absolute inset-0 w-full h-full"
      />
      {/* [MANIFESTO] censor bar — pure black, sharp corners, no shadow, anchored
          to the baked eye-line so it censors the eyes at every viewport size.
          This is the master brand element (DESIGN.md §1); it stays on the face
          and is never replaced by a score. */}
      <div
        data-testid="manifesto-bar"
        className="absolute bg-obsidian flex items-center justify-center"
        style={{
          left: '-4%',
          right: '-4%',
          top: `${heroMesh.eyeLineY * 100}%`,
          height: '7.2%',
          transform: 'translateY(-50%)',
        }}
      >
        <span
          className="font-display uppercase text-aurum font-medium whitespace-nowrap"
          style={{ fontSize: 'clamp(9px, 3.4cqw, 17px)', letterSpacing: '0.42em', paddingLeft: '0.42em' }}
        >
          {COPY.MARKETING_MANIFESTO}
        </span>
      </div>
      {vignette && (
        // Fixed frame so the photo's charcoal backdrop melts fully into the
        // obsidian page.
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 62% 56% at 50% 44%, transparent 36%, rgba(0,0,0,0.55) 68%, #000 96%)',
            boxShadow: 'inset 0 0 120px 60px #000',
          }}
        />
      )}
    </div>
  );
};

export default MeshPortrait;
